import React from 'react'

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import humanize from 'humanize-string'
import { StepType as StepTypeApi } from 'types/graphql'
import { StepType } from 'web/src/graphql/types/steps'

import {
  DocumentCategory,
  DocumentCategoryLabel,
} from 'src/graphql/types/documents'

dayjs.extend(relativeTime)
dayjs.extend(utc)

const MAX_STRING_LENGTH = 150

export const formatEnum = (values: string | string[] | null | undefined) => {
  let output = ''

  if (Array.isArray(values)) {
    const humanizedValues = values.map((value) => humanize(value))
    output = humanizedValues.join(', ')
  } else if (typeof values === 'string') {
    output = humanize(values)
  }

  return output
}

export const jsonDisplay = (obj: unknown) => {
  return (
    <pre>
      <code>{JSON.stringify(obj, null, 2)}</code>
    </pre>
  )
}

export const truncate = (value: string | number) => {
  let output = value?.toString() ?? ''

  if (output.length > MAX_STRING_LENGTH) {
    output = output.substring(0, MAX_STRING_LENGTH) + '...'
  }

  return output
}

export const jsonTruncate = (obj: unknown) => {
  return truncate(JSON.stringify(obj, null, 2))
}

export const timeTag = (dateTime?: string) => {
  let output: string | JSX.Element = ''

  if (dateTime) {
    output = (
      <time dateTime={dateTime} title={dateTime}>
        {new Date(dateTime).toUTCString()}
      </time>
    )
  }

  return output
}

export const checkboxInputTag = (checked: boolean) => {
  return <input type="checkbox" checked={checked} disabled />
}

export const formatSnakeValue = (value: string) => {
  if (!value) return value

  return value
    .toLowerCase() // Convert to lower case
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (l) => l.toUpperCase()) // Capitalize the first letter of each word
}

export const formatCamelValue = (value) => {
  if (!value) return value

  return value
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
}

export const formatExperience = (
  experience: number | null | undefined,
  shortMode = false
) => {
  if (experience == null) return null

  const years = Math.floor(experience)
  const months = Math.round((experience - years) * 12)
  const yearLabel = shortMode ? 'y' : years === 1 ? 'year' : 'years'
  const monthLabel = shortMode ? 'm' : months === 1 ? 'month' : 'months'

  const parts = []
  if (years > 0) parts.push(`${years} ${yearLabel}`)
  if (months > 0) parts.push(`${months} ${monthLabel}`)

  return (
    parts.join(shortMode ? ', ' : ', ') || (shortMode ? '< 1 m' : '< 1 month')
  )
}

export const formatBytes = (bytes) => {
  if (bytes < 1024) {
    return bytes + ' B'
  } else if (bytes < 1048576) {
    return (bytes / 1024).toFixed(1) + ' KB'
  } else {
    return (bytes / 1048576).toFixed(1) + ' MB'
  }
}

export const formatSidebarAddress = (address) => {
  return address ? `${address.city}, ${address.state}` : '-'
}

/**
 * Joins a user's name parts into a single display string.
 *
 * Returns null when the user is null/undefined OR when every name part is
 * missing — this lets callers `?? <fallback>` cleanly. Falsy parts (null,
 * undefined, empty string) are filtered out, so a user with only a first
 * name returns "John" rather than "John  null" or "John undefined".
 */
export const buildFullName = (
  user:
    | {
        firstName?: string | null
        middleName?: string | null
        lastName?: string | null
      }
    | null
    | undefined
): string | null => {
  if (!user) return null
  const parts = [user.firstName, user.middleName, user.lastName].filter(
    (p): p is string => Boolean(p) && (p as string).trim().length > 0
  )
  return parts.length > 0 ? parts.join(' ') : null
}

/**
 * Get initials from a full name string.
 * Takes the first character of the first word and the first character of the last word.
 * For single-word names, returns the first two characters.
 * @param fullName - The full name string (e.g., "John Doe" or "Iren Azra Coskun Zou")
 * @returns The initials (e.g., "JD" or "IZ")
 */
export const getInitials = (fullName: string | null | undefined): string => {
  if (!fullName) return ''
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0]?.[0] || ''}${
    parts[parts.length - 1]?.[0] || ''
  }`.toUpperCase()
}

export const formatDate = (date: string | Date): string => {
  const inputDate = dayjs(date)
  const now = dayjs()

  if (now.diff(inputDate, 'day') < 7) {
    // For dates less than 7 days old, return a relative time string
    const descriptor = inputDate < now ? 'ago' : 'from now'
    return inputDate.fromNow(true) + ' ' + descriptor
  } else {
    // For older dates, return a formatted date string
    return inputDate.format('MM/DD/YYYY')
  }
}

/**
 * Compact relative date for tight table columns.
 * Examples: "now", "5 mins", "1 hour", "3 days", "01/15/25".
 * Under 7 days returns short relative time; older returns MM/DD/YY.
 * No "ago" suffix — column context implies past dates.
 */
export const formatDateCompact = (date: string | Date): string => {
  if (!date) return '-'
  const inputDate = dayjs(date)
  if (!inputDate.isValid()) return '-'

  const now = dayjs()
  const diffMinutes = now.diff(inputDate, 'minute')
  const diffHours = now.diff(inputDate, 'hour')
  const diffDays = now.diff(inputDate, 'day')

  if (diffMinutes < 1) return 'now'
  if (diffMinutes < 60)
    return `${diffMinutes} ${diffMinutes === 1 ? 'min' : 'mins'}`
  if (diffHours < 24)
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`
  return inputDate.format('MM/DD/YY')
}

export const formatDateWithTime = (date: string | Date): string => {
  const inputDate = dayjs(date)
  const now = dayjs()

  if (now.diff(inputDate, 'day') < 7) {
    // For dates less than 7 days old, return a relative time string
    const descriptor = inputDate < now ? 'ago' : 'from now'
    return inputDate.fromNow(true) + ' ' + descriptor
  } else {
    // For older dates, return a formatted date string
    return inputDate.format('MM/DD/YYYY hh:mm A')
  }
}

export const formatDateCustom = (
  date: string | Date,
  format: string
): string => {
  return dayjs(date).format(format)
}

export const formatDateSimple = (date: string | Date): string => {
  if (!date) return '-'

  // Parse as UTC to avoid timezone shifts for date-only values
  const inputDate = dayjs.utc(date)

  // Check if the date is valid
  if (!inputDate.isValid()) {
    return '-'
  }

  return inputDate.format('MM/DD/YYYY')
}

/**
 * Format date as MM/DD/YYYY for display (always absolute, no relative)
 */
export const formatDateAbsolute = (date: string | Date): string => {
  if (!date) return '-'
  const inputDate = dayjs(date)
  if (!inputDate.isValid()) return '-'
  return inputDate.format('MM/DD/YYYY')
}

/**
 * Compact, conversational timestamp for chat-style SMS bubbles.
 * Examples: "Today 2:45 PM", "Yesterday 9:00 AM", "Jun 24, 2:45 PM",
 * "Jun 24, 2021, 2:45 PM" (older than the current year). The exact timestamp
 * is shown on hover via formatDateTimeForTooltip.
 */
export const formatSmsTimestamp = (date: string | Date): string => {
  if (!date) return '-'
  const inputDate = dayjs(date)
  if (!inputDate.isValid()) return '-'

  const now = dayjs()
  const time = inputDate.format('h:mm A')

  if (inputDate.isSame(now, 'day')) return `Today ${time}`
  if (inputDate.isSame(now.subtract(1, 'day'), 'day'))
    return `Yesterday ${time}`
  if (inputDate.isSame(now, 'year'))
    return `${inputDate.format('MMM D')}, ${time}`
  return `${inputDate.format('MMM D, YYYY')}, ${time}`
}

/**
 * Format date with full timestamp for tooltip display
 * Returns format like "01/15/2025 at 2:30:45 PM"
 */
export const formatDateTimeForTooltip = (date: string | Date): string => {
  if (!date) return '-'
  const inputDate = dayjs(date)
  if (!inputDate.isValid()) return '-'
  return inputDate.format('MM/DD/YYYY [at] h:mm:ss A')
}

export const StepTypeLabels = {
  [StepType.DOT_APP]: { fullName: 'Application', shortName: 'Application' },
  [StepType.MVR]: { fullName: 'MVR', shortName: 'MVR' },
  [StepType.PSP]: { fullName: 'PSP', shortName: 'PSP' },
  [StepType.BACKGROUND_CHECK]: {
    fullName: 'Background Check',
    shortName: 'Background',
  },
  [StepType.CDL]: { fullName: 'CDL', shortName: 'CDL' },
  [StepType.CDLIS]: { fullName: 'CDLIS', shortName: 'CDLIS' },
  [StepType.VERIFICATION_OF_EMPLOYMENT]: { fullName: 'VOE', shortName: 'VOE' },
  [StepType.DOT_MEDICAL]: { fullName: 'DOT Med Card', shortName: 'Med Card' },
  [StepType.CLEARINGHOUSE]: {
    fullName: 'Clearinghouse',
    shortName: 'Clearinghouse',
  },
  [StepType.DRUG_AND_ALCOHOL_TESTING]: {
    fullName: 'Drug & Alcohol Testing',
    shortName: 'Drug & Alcohol',
  },
  [StepType.ROAD_TEST]: { fullName: 'Road Test', shortName: 'Road Test' },
}

export const formatStepType = (type: StepTypeApi, useShortName = false) => {
  if (!type) return type

  if (type in StepTypeLabels) {
    return useShortName
      ? StepTypeLabels[type].shortName
      : StepTypeLabels[type].fullName
  }

  return formatSnakeValue(type)
}

export const getStepLabel = (step, useShortName = false) => {
  if (step?.customStep?.label) return step.customStep.label
  return formatStepType(step?.type ?? step?.stepType, useShortName)
}

export const calculateStageBadgeColor = (stage: string) => {
  switch (stage) {
    case 'PROSPECT':
      return 'gray'
    case 'HIRED':
      return 'green'
    case 'TERMINATED':
      return 'red'
    case 'DISQUALIFIED':
      return 'orange'
    case 'NOT_INTERESTED':
      return 'orange'
    default:
      return 'gray'
  }
}

export const formatDocumentCategory = (category: DocumentCategory) => {
  if (!category) return category

  if (category in DocumentCategoryLabel) {
    return DocumentCategoryLabel[category]
  }

  return formatSnakeValue(category)
}

// ---------------------------------------------------------------------------
// Analytics formatting utilities
// ---------------------------------------------------------------------------

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Convert "2026-01" to "Jan '26" or "2026-01-05" to "Jan 5".
 *
 * Pass 'long' to spell the year out — "Jan 2026" and "Jan 5, 2026". Chart axes
 * want the short form; anything a driver reads as a date wants the long one.
 *
 * Anything other than 'long' is treated as 'short', so this stays safe to hand
 * straight to a chart library that calls its formatter with extra arguments.
 */
export const formatMonth = (
  value: string,
  style: 'short' | 'long' = 'short'
): string => {
  const parts = value.split('-')
  const monthIndex = parseInt(parts[1], 10) - 1
  const month = MONTH_SHORT[monthIndex] ?? parts[1]
  const year = parts[0]
  const isLong = style === 'long'

  if (parts.length === 3) {
    const day = parseInt(parts[2], 10)
    return isLong ? `${month} ${day}, ${year}` : `${month} ${day}`
  }

  return isLong ? `${month} ${year}` : `${month} '${year.slice(2)}`
}

/**
 * Format a number as a percentage string, e.g. 72.5 -> "72.5%"
 */
export const formatPercent = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}%`
}

/**
 * Format a number as days, e.g. 3.2 -> "3.2d"
 */
export const formatDays = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}d`
}
